/* eslint-disable @typescript-eslint/require-array-sort-compare */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Accordion, AccordionSummary, Box, Divider, FormControlLabel, Switch, Typography, AccordionDetails, useTheme } from '@mui/material'
import GenericModal from '../../../../components/GenericModal'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { ButtonSize, ButtonType } from '../../../../components/GenericButton/styles'
import { useAppDispatch } from '../../../../store'
import { type ISurveySection, setfilters } from '../../../../store/slices/SurveyTemplateSlice'
import { getSurveySections } from '../../../../store/slices/SurveyTemplateSlice/selectors'
import { FormProvider, type SubmitHandler, useForm, Controller } from 'react-hook-form'
import { grey } from '../../../../colors'
import { type IAttribute } from '../../../../models/Attributes'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import Branching from './Branching'
import { BranchingOperation, SurveyItemQuestionType } from '../../types'
import {
  BranchingOperationsDropdown,
  TEACHING_ROLE_OPTIONS,
  DEFAULT_REPEAT_PER_INSTRUCTOR_ROLES,
  sortTeachingRolesByPrecedence,
  type TeachingRole,
  type TeachingRoleOption,
  sortTeachingRoleOptionsByPrecedence
} from '../../constant'
import { findParentSectionAndQuestion } from '../../helpers'
import Checkbox from '@mui/material/Checkbox'
import { ReactComponent as SelectedCheckBox } from '../../../../assets/check-box-selected.svg'
import _ from 'lodash'
import { notification } from '../../../../components/Notifications'
import AttributesFilterForGroupSetting from '../AttributesFilterForGroupSetting'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AttributesFilterChips from '../../../../components/AttributesFilterChips'
import GenericButton from '../../../../components/GenericButton'
import CustomTooltip from '../../../../components/CustomTooltip'
import { ReactComponent as DeleteIcon } from '../../../../assets/trash.svg'
import EditIcon from '@mui/icons-material/Edit'
import MultiSelectWithCheckBox, { RenderTagsType } from '../../../../components/FormElements/MultiSelectWithCheckBox'

const icon = <Box border={`1px solid ${grey[350]}`} borderRadius="4px" width="18px" height="18px" />

interface SectionFiltersModalProps {
  section: ISurveySection
  isFilterModalOpen: boolean
  closeFilterModal: () => void
}

export interface ICourseFilters {
  key: IAttribute
  op: string
  value: string[]
}

interface FormData {
  courseFilters: ICourseFilters[]
  isRepeatPerInstructor: boolean
  isSelectInstructorEnabled: boolean
  selectedTeachingRoles: TeachingRoleOption[]
  branchingFilters?: any[]
}

const mapTeachingRoleValuesToOptions = (selectedRoleValues: TeachingRole[] = []): TeachingRoleOption[] => {
  return sortTeachingRoleOptionsByPrecedence(
    TEACHING_ROLE_OPTIONS.filter((option) => selectedRoleValues.includes(option.value))
  )
}

// Sorts teaching roles and falls back to the standard default pair when none are present.
const getNormalizedTeachingRoles = (selectedRoleValues: TeachingRole[] = []): TeachingRole[] => {
  const normalizedRoles = sortTeachingRolesByPrecedence(selectedRoleValues)
  return normalizedRoles.length > 0 ? normalizedRoles : DEFAULT_REPEAT_PER_INSTRUCTOR_ROLES
}

// When a parent section repeats per instructor, branched children inherit its effective roles and locked-on state.
const getParentLockedBranchSettings = (parentSection?: ISurveySection): {
  isRepeatPerInstructor: boolean
  selectedRoles: TeachingRole[]
} => {
  const parentSettings = parentSection?.settings
  const parentDefaultSetting = Array.isArray(parentSettings) ? parentSettings[0] : undefined
  const isParentRepeatPerInstructor = parentDefaultSetting?.isRepeatPerInstructor ?? false

  if (!isParentRepeatPerInstructor) {
    return {
      isRepeatPerInstructor: false,
      selectedRoles: []
    }
  }

  return {
    isRepeatPerInstructor: true,
    selectedRoles: getNormalizedTeachingRoles(parentDefaultSetting?.selectedRoles ?? [])
  }
}

// Resolves the settings shown in the form, including inherited locked settings for branched sections.
const getEffectiveSectionDefaultSetting = (section: ISurveySection, parentSection?: ISurveySection): {
  isRepeatPerInstructor: boolean
  isSelectInstructorEnabled: boolean
  selectedRoles: TeachingRole[]
} => {
  if (section?.parentSurveySectionId) {
    const parentLockedSettings = getParentLockedBranchSettings(parentSection)

    if (parentLockedSettings.isRepeatPerInstructor) {
      return {
        isRepeatPerInstructor: true,
        isSelectInstructorEnabled: false,
        selectedRoles: parentLockedSettings.selectedRoles
      }
    }
  }

  const sectionSettings = section?.settings
  const sectionDefaultSetting = Array.isArray(sectionSettings) ? sectionSettings[0] : undefined

  return {
    isRepeatPerInstructor: sectionDefaultSetting?.isRepeatPerInstructor ?? false,
    isSelectInstructorEnabled: sectionDefaultSetting?.isSelectInstructorEnabled ?? false,
    selectedRoles: sectionDefaultSetting?.selectedRoles ?? []
  }
}

const getBranchingDefaultFilters = (section: ISurveySection) => ([
  {
    sectionTitle: section?.title ?? '',
    questionPrompt: section?.branchingFilters?.prompt ?? '',
    operation: BranchingOperationsDropdown.find((b) => b.value === section?.branchingFilters?.operation) ?? null,
    answers: section?.branchingFilters?.answer ?? []
  }
])

// Settings-only saves for branched sections should not trigger branch regrouping.
const hasBranchingFiltersChanged = (
  section: ISurveySection,
  branchingFilter?: { operation?: string, answer?: string[] } | null
): boolean => {
  const currentOperation = section?.branchingFilters?.operation ?? null
  const currentAnswers = section?.branchingFilters?.answer ?? []
  const nextOperation = branchingFilter?.operation ?? null
  const nextAnswers = branchingFilter?.answer ?? []

  return currentOperation !== nextOperation || !_.isEqual(currentAnswers, nextAnswers)
}

const getDefaultFormValues = (section: ISurveySection, sectionDefaultSetting: any) => ({
  isRepeatPerInstructor: sectionDefaultSetting?.isRepeatPerInstructor ?? false,
  isSelectInstructorEnabled: sectionDefaultSetting?.isSelectInstructorEnabled ?? false,
  selectedTeachingRoles: mapTeachingRoleValuesToOptions(sectionDefaultSetting?.selectedRoles ?? []),
  courseFilters: [],
  branchingFilters: getBranchingDefaultFilters(section)
})

export const SectionFiltersModal: React.FC<SectionFiltersModalProps> = ({ section, isFilterModalOpen, closeFilterModal }) => {
  const sectionsList = getSurveySections()
  const { parentSection, parentQuestion } = findParentSectionAndQuestion(sectionsList, section) ?? {}
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const settings = useMemo(() => (Array.isArray(section?.settings) ? section.settings : []), [section?.settings])
  // Branched sections can be forced on by the parent, so the form defaults need to reflect inheritance before render.
  const sectionDefaultSetting = useMemo(
    () => getEffectiveSectionDefaultSetting(section, parentSection),
    [parentSection, section, settings]
  )
  // Keep the inherited parent state separate so the UI can lock branch controls without mutating local values.
  const parentLockedBranchSettings = useMemo(
    () => getParentLockedBranchSettings(parentSection),
    [parentSection]
  )
  const isParentRepeatPerInstructor = parentLockedBranchSettings.isRepeatPerInstructor
  const parentTeachingRoleOptions = useMemo(
    () => mapTeachingRoleValuesToOptions(parentLockedBranchSettings.selectedRoles),
    [parentLockedBranchSettings.selectedRoles]
  )
  const [draftSettings, setDraftSettings] = useState(settings)
  const [editingSettingIndex, setEditingSettingIndex] = useState<number | null>(null)
  const [deleteSettingIndex, setDeleteSettingIndex] = useState<number | null>(null)
  const previousIsRepeatPerInstructorRef = useRef<boolean>(sectionDefaultSetting?.isRepeatPerInstructor ?? false)

  const validationSchema = yup.object().shape({
    ...(section?.parentSurveySectionId
      ? {
          branchingFilters: yup.array().of(
            yup.object().shape({
              operation: yup.object().shape({
                value: yup.string().required('Operation is required')
              }).required('Operation is required'),
              answers: yup.array().when('operation.value', {
                is: (operationValue: string) => operationValue === BranchingOperation.Is,
                then: schema => schema
                  .min(1, 'At least one answer is required when operation is "Is"')
                  .required('Answers are required for the "Is" operation'),
                otherwise: schema => schema.notRequired().nullable()
              })
            })
          ),
          isRepeatPerInstructor: yup.boolean(),
          selectedTeachingRoles: yup.array().of(
            yup.object().shape({
              value: yup.string().required(),
              label: yup.string().required()
            })
          ).when('isRepeatPerInstructor', {
            is: true,
            then: schema =>
              schema
                .min(1, 'At least one teaching role is required')
                .required('Teaching roles are required when "Repeat per Instructor" is enabled'),
            otherwise: schema => schema.notRequired()
          })
        }
      : {
          courseFilters: yup.array().of(
            yup.object().shape({
              key: yup.object().required('Attribute is required'),
              op: yup.string().required('Operation is required'),
              value: yup.array().min(1, 'At least one value is required').required('Value is required')
            })
          ),
          isRepeatPerInstructor: yup.boolean(),
          selectedTeachingRoles: yup.array().of(
            yup.object().shape({
              value: yup.string().required(),
              label: yup.string().required()
            })
          ).when('isRepeatPerInstructor', {
            is: true,
            then: schema =>
              schema
                .min(1, 'At least one teaching role is required')
                .required('Teaching roles are required when "Repeat per Instructor" is enabled'),
            otherwise: schema => schema.notRequired()
          })
        })
  })

  const methods = useForm<any>({
    defaultValues: getDefaultFormValues(section, sectionDefaultSetting),
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  })

  const { handleSubmit, control, watch, setValue, getValues, trigger } = methods

  const isRepeatPerInstructor = watch('isRepeatPerInstructor')
  const selectedTeachingRoleOptions = watch('selectedTeachingRoles')
  const isSelectInstructorEnabled = watch('isSelectInstructorEnabled')
  const courseFilters = watch('courseFilters')
  const hasPendingFiltersDraft = Array.isArray(courseFilters) &&
    courseFilters.some((filter) => Array.isArray(filter?.value) && filter.value.length > 0)

  const applyGlobalSettingsToAllGroups = useCallback((
    groups: any[],
    repeatPerInstructor: boolean,
    selectInstructorEnabled: boolean,
    selectedTeachingRoles: TeachingRoleOption[]
  ) => groups.map((group) => ({
    ...group,
    isRepeatPerInstructor: repeatPerInstructor,
    isSelectInstructorEnabled: selectInstructorEnabled,
    selectedRoles: selectedTeachingRoles.map((role) => role.value)
  })), [])

  useEffect(() => {
    if (!isRepeatPerInstructor) {
      if (isSelectInstructorEnabled) {
        setValue('isSelectInstructorEnabled', false)
      }

      if (Array.isArray(selectedTeachingRoleOptions) && selectedTeachingRoleOptions.length > 0) {
        setValue('selectedTeachingRoles', [])
      }
    } else if (!previousIsRepeatPerInstructorRef.current && (!Array.isArray(selectedTeachingRoleOptions) || selectedTeachingRoleOptions.length === 0)) {
      setValue('selectedTeachingRoles', mapTeachingRoleValuesToOptions(DEFAULT_REPEAT_PER_INSTRUCTOR_ROLES))
    }
    previousIsRepeatPerInstructorRef.current = isRepeatPerInstructor
  }, [isRepeatPerInstructor, isSelectInstructorEnabled, selectedTeachingRoleOptions, setValue])

  useEffect(() => {
    setDraftSettings((prev) => applyGlobalSettingsToAllGroups(
      prev,
      isRepeatPerInstructor,
      isSelectInstructorEnabled,
      sortTeachingRoleOptionsByPrecedence(selectedTeachingRoleOptions ?? [])
    ))
  }, [applyGlobalSettingsToAllGroups, isRepeatPerInstructor, isSelectInstructorEnabled, selectedTeachingRoleOptions])

  useEffect(() => {
    const globalRepeat = sectionDefaultSetting?.isRepeatPerInstructor ?? false
    const globalSelect = sectionDefaultSetting?.isSelectInstructorEnabled ?? false
    const globalTeachingRoles = mapTeachingRoleValuesToOptions(sectionDefaultSetting?.selectedRoles ?? [])
    setDraftSettings(applyGlobalSettingsToAllGroups(settings, globalRepeat, globalSelect, globalTeachingRoles))
    setEditingSettingIndex(null)
  }, [applyGlobalSettingsToAllGroups, settings, section?.surveySectionId, sectionDefaultSetting?.isRepeatPerInstructor, sectionDefaultSetting?.isSelectInstructorEnabled, sectionDefaultSetting?.selectedRoles])

  const buildPreparedSettings = useCallback((values: FormData) => {
    const preparedAttributes = values?.courseFilters?.map((attribute) => ({
      op: attribute.op,
      values: attribute?.value,
      type: attribute.key?.attributeType,
      key: attribute.key?.attributeKey
    })) ?? []

    return {
      isRepeatPerInstructor: values.isRepeatPerInstructor,
      selectedRoles: sortTeachingRoleOptionsByPrecedence(values?.selectedTeachingRoles ?? []).map((role) => role.value),
      isSelectInstructorEnabled: values.isSelectInstructorEnabled,
      attributes: preparedAttributes
    }
  }, [])

  const areSettingsDuplicateByAttributes = useCallback((setting: any, preparedSettings: any) => {
    return _.isEqual(setting?.attributes ?? [], preparedSettings?.attributes ?? [])
  }, [])

  const hasDuplicateAttributeConditionsInGroup = useCallback((preparedSettings: any) => {
    const attributes = preparedSettings?.attributes ?? []
    const seenConditions = new Set<string>()

    for (const attribute of attributes) {
      const normalizedValues = Array.isArray(attribute?.values) ? [...attribute.values].sort() : []
      const conditionKey = `${attribute?.key ?? ''}|${attribute?.op ?? ''}|${normalizedValues.join(',')}`

      if (seenConditions.has(conditionKey)) {
        return true
      }

      seenConditions.add(conditionKey)
    }

    return false
  }, [])

  const hasAttributes = useCallback((values: FormData) => (values?.courseFilters?.length ?? 0) > 0, [])

  const hasGlobalSelection = useCallback((values: FormData) => values.isRepeatPerInstructor || values.isSelectInstructorEnabled, [])

  const isEmptyAttributesGroup = useCallback((setting: any) => !Array.isArray(setting?.attributes) || setting.attributes.length === 0, [])

  const hasSettingContent = useCallback((values: FormData) => {
    return hasAttributes(values)
  }, [hasAttributes])

  const upsertSettingGroup = useCallback((groups: any[], preparedSettings: any, values: FormData, editingIndex?: number | null) => {
    const nextGroups = [...groups]

    if (typeof editingIndex === 'number') {
      nextGroups[editingIndex] = preparedSettings
      return nextGroups
    }

    if (hasAttributes(values)) {
      const firstEmptyGroupIndex = nextGroups.findIndex((group) => isEmptyAttributesGroup(group))
      if (firstEmptyGroupIndex !== -1) {
        nextGroups[firstEmptyGroupIndex] = preparedSettings
        return nextGroups
      }

      nextGroups.push(preparedSettings)
      return nextGroups
    }

    if (nextGroups.length === 0 && hasGlobalSelection(values)) {
      nextGroups.push(preparedSettings)
    }

    return nextGroups
  }, [hasAttributes, hasGlobalSelection, isEmptyAttributesGroup])

  const handleAddFiltersGroup = useCallback(async () => {
    const isCourseFiltersValid = await trigger('courseFilters')
    if (!isCourseFiltersValid) {
      notification('Please complete all filter fields before adding/updating the condition group', { variant: 'error' })
      return
    }

    const values = getValues()
    const preparedSettings = buildPreparedSettings(values)
    const isDuplicateSetting = draftSettings.some((setting, index) => {
      if (index === editingSettingIndex) {
        return false
      }

      return areSettingsDuplicateByAttributes(setting, preparedSettings)
    })

    if (!hasSettingContent(values)) {
      notification('Add at least one filter before creating a new group', { variant: 'error' })
      return
    }

    if (hasDuplicateAttributeConditionsInGroup(preparedSettings)) {
      notification('Same attribute condition already added in this group', { variant: 'error' })
      return
    }

    if (isDuplicateSetting) {
      notification('This condition group already exists', { variant: 'error' })
      return
    }

    if (editingSettingIndex !== null) {
      setDraftSettings((prev) => upsertSettingGroup(prev, preparedSettings, values, editingSettingIndex))
      setEditingSettingIndex(null)
    } else {
      setDraftSettings((prev) => upsertSettingGroup(prev, preparedSettings, values))
    }

    setValue('courseFilters', [])
  }, [areSettingsDuplicateByAttributes, buildPreparedSettings, draftSettings, editingSettingIndex, getValues, hasDuplicateAttributeConditionsInGroup, hasSettingContent, setValue, trigger, upsertSettingGroup])

  const handleDeleteFiltersGroup = useCallback((settingIndex: number) => {
    setDraftSettings((prev) => prev.filter((_, index) => index !== settingIndex))

    if (editingSettingIndex === settingIndex) {
      setEditingSettingIndex(null)
      setValue('courseFilters', [])
    }
  }, [editingSettingIndex, setValue])

  const handleOpenDeleteConfirmation = useCallback((settingIndex: number) => {
    setDeleteSettingIndex(settingIndex)
  }, [])

  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteSettingIndex(null)
  }, [])

  const handleConfirmDeleteFiltersGroup = useCallback(async () => {
    if (deleteSettingIndex === null) {
      return
    }

    handleDeleteFiltersGroup(deleteSettingIndex)
    setDeleteSettingIndex(null)
  }, [deleteSettingIndex, handleDeleteFiltersGroup])

  const handleCancelFiltersGroup = useCallback(() => {
    setEditingSettingIndex(null)
    setValue('courseFilters', [])
  }, [setValue])

  const handleEditFiltersGroup = useCallback((settingIndex: number) => {
    if (hasPendingFiltersDraft && editingSettingIndex !== settingIndex) {
      notification('Please add, update or cancel pending condition group before saving.', { variant: 'error' })
      return
    }

    const setting = draftSettings[settingIndex]
    if (!setting) {
      return
    }

    setEditingSettingIndex(settingIndex)
    setValue('isRepeatPerInstructor', setting?.isRepeatPerInstructor ?? false)
    setValue('isSelectInstructorEnabled', setting?.isSelectInstructorEnabled ?? false)
    setValue('selectedTeachingRoles', mapTeachingRoleValuesToOptions(setting?.selectedRoles ?? []))
  }, [draftSettings, editingSettingIndex, hasPendingFiltersDraft, setValue])

  const onSubmit: SubmitHandler<FormData> = useCallback(async (values) => {
    if (!section?.parentSurveySectionId && (hasPendingFiltersDraft || editingSettingIndex !== null)) {
      notification('Please add, update or cancel pending condition group before saving.', { variant: 'error' })
      return
    }

    const sortedSelectedTeachingRoles = sortTeachingRoleOptionsByPrecedence(values?.selectedTeachingRoles ?? [])
    const preparedSettings = buildPreparedSettings(values)
    const branchingFilters = values?.branchingFilters?.map((_branchingFilter) => ({
      operation: _branchingFilter?.operation?.value,
      prompt: _branchingFilter?.questionPrompt,
      answer: _branchingFilter?.itemType === SurveyItemQuestionType.LINEAR_SCALE
        ? _branchingFilter.answers?.map((a: any) => a.option)
        : _branchingFilter.answers
    }))

    if (section?.parentSurveySectionId) {
      // Branched sections only persist repeat-per-instructor and selected roles; if the parent is on, those values stay inherited.
      const branchSettings = isParentRepeatPerInstructor
        ? [{
            isRepeatPerInstructor: true,
            selectedRoles: getNormalizedTeachingRoles(parentLockedBranchSettings.selectedRoles)
          }]
        : (values.isRepeatPerInstructor
            ? [{
                isRepeatPerInstructor: true,
                selectedRoles: sortedSelectedTeachingRoles.map((role) => role.value)
              }]
            : [{ isRepeatPerInstructor: false }])
      const nextBranchingFilter = branchingFilters?.[0]
      const shouldUpdateBranchingFilters = hasBranchingFiltersChanged(section, nextBranchingFilter)

      dispatch(setfilters({
        surveySectionId: section.surveySectionId,
        settings: branchSettings,
        branchingFilters: shouldUpdateBranchingFilters
          ? {
              filters: nextBranchingFilter,
              parentSection,
              parentQuestion
            }
          : undefined
      }))
      closeFilterModal()
      return
    }

    const isDuplicateSetting = hasAttributes(values) && draftSettings.some((setting, index) => {
      if (index === editingSettingIndex) {
        return false
      }

      return areSettingsDuplicateByAttributes(setting, preparedSettings)
    })
    if (hasDuplicateAttributeConditionsInGroup(preparedSettings)) {
      notification('Same attribute condition already added in this group', { variant: 'error' })
      return
    }
    if (isDuplicateSetting) {
      notification('This condition group already exists', { variant: 'error' })
      return
    }
    const nextSettings = upsertSettingGroup(draftSettings, preparedSettings, values, editingSettingIndex)

    const normalizedSettings = applyGlobalSettingsToAllGroups(
      nextSettings,
      values.isRepeatPerInstructor,
      values.isSelectInstructorEnabled,
      sortedSelectedTeachingRoles
    )

    dispatch(setfilters({
      surveySectionId: section.surveySectionId,
      settings: normalizedSettings,
      isSelectInstructorEnabled: values.isSelectInstructorEnabled,
      branchingFilters: {
        filters: branchingFilters?.[0],
        parentSection,
        parentQuestion
      }
    }))
    closeFilterModal()
  }, [applyGlobalSettingsToAllGroups, areSettingsDuplicateByAttributes, buildPreparedSettings, closeFilterModal, dispatch, draftSettings, editingSettingIndex, hasAttributes, hasDuplicateAttributeConditionsInGroup, hasPendingFiltersDraft, isParentRepeatPerInstructor, parentLockedBranchSettings.selectedRoles, parentQuestion, parentSection, section, upsertSettingGroup])

  useEffect(() => {
    if (section) {
      methods.reset(getDefaultFormValues(section, sectionDefaultSetting))
    }
  }, [section, methods, sectionDefaultSetting])

  return (
    <>
      <GenericModal
        title="Settings"
        showCloseIcon={false}
        action={false}
        maxWidth="lg"
        isOpen={isFilterModalOpen}
        onClose={closeFilterModal}
        // This modal hosts the survey display-conditions Values popper; enforcing focus here
        // breaks hover and arrow-key navigation inside the portaled autocomplete list.
        disableEnforceFocus
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
          {!section?.parentSurveySectionId && (
            <>
              <Box display="flex" alignItems="center">
                <Controller
                  name="isRepeatPerInstructor"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={field.onChange}
                      color="primary"
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  )}
                />
                <Typography sx={{ ml: 2.5, fontWeight: 500 }}>
                  Repeat per Instructor ({isRepeatPerInstructor ? 'On' : 'Off'})
                </Typography>
              </Box>
              <Box mt={2} mb={2}>
                <Typography sx={{ fontWeight: 500, mb: 1 }}>
                  Teaching Roles
                </Typography>
                <MultiSelectWithCheckBox
                  name="selectedTeachingRoles"
                  options={TEACHING_ROLE_OPTIONS}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  onSelectionChange={(selected) => {
                    setValue('selectedTeachingRoles', sortTeachingRoleOptionsByPrecedence(selected), { shouldDirty: true })
                  }}
                  placeholder="Select teaching roles"
                  renderTagsType={RenderTagsType.Chips}
                  showSelectAll
                  disabled={!isRepeatPerInstructor}
                  description='Choose which teaching roles this section should be repeated for.'
                  descriptionSx={{ color: grey[850] }}
                />
              </Box>
              <Box display="flex" alignItems="flex-start">
                <Controller
                  name="isSelectInstructorEnabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checkedIcon={<SelectedCheckBox width="18px" height="18px" />}
                          icon={icon}
                          checked={field.value}
                          onChange={field.onChange}
                          sx={{ ml: 1 }}
                          disabled={!isRepeatPerInstructor}
                        />
                      }
                      label=""
                    />
                  )}
                />
                <Box>
                  <Typography sx={{ ml: 0, fontWeight: 500 }}>
                    Allow users to select which instructors to evaluate
                  </Typography>
                  <Typography sx={{ ml: 0, color: grey[850] }}>
                    Before filling out this section, users will be prompted to select which instructors they want to evaluate.
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mt: 4 }} />
              {
                draftSettings.some((setting) => Array.isArray(setting?.attributes) && setting.attributes.length > 0) && (
                  <Accordion

                    disableGutters
                    elevation={0}
                    defaultExpanded={true}
                    sx={{
                      mt: 4,
                      border: `1px solid ${theme.palette.borderColor}`,
                      borderRadius: theme.spacing(2),
                      '&::before': {
                        display: 'none'
                      }
                    }}
                  >

                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: grey[250] }} />}
                    >
                      <Typography fontSize={14} fontWeight={600} color={grey[250]}>
                        Display condition groups
                      </Typography>
                    </AccordionSummary>
                    {draftSettings.map((setting, settingIndex: number) => (

                      <AccordionDetails key={settingIndex} sx={{ px: 3, pb: 3, pt: 0 }}>
                        <Box display="flex" width={'100%'} justifyContent="space-between" alignItems="center" gap={2}>
                          <Box display={'flex'} gap={2} alignItems={'center'}>
                            <Typography fontSize={14} fontWeight={600} color={grey[250]} sx={{ whiteSpace: 'nowrap' }}>
                              Group {settingIndex + 1}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" minWidth={0} minHeight={32}>
                              {setting?.attributes && setting.attributes.length > 0 && <AttributesFilterChips attributes={setting?.attributes ?? []} />}
                            </Box>
                          </Box>
                          <Box display={'flex'} justifyContent="flex-end" gap={1} mt={2}>
                            <CustomTooltip
                              title={hasPendingFiltersDraft && editingSettingIndex !== settingIndex
                                ? 'Complete, update, or cancel current draft conditions before editing another group.'
                                : 'Edit this condition group.'}
                            >
                              <GenericButton
                                buttonType={ButtonType.Ghost}
                                buttonSize={ButtonSize.Medium}
                                icon={<EditIcon />}
                                onClick={() => { handleEditFiltersGroup(settingIndex) }}
                                disabled={hasPendingFiltersDraft && editingSettingIndex !== settingIndex}
                              />
                            </CustomTooltip>

                            <CustomTooltip
                              title={hasPendingFiltersDraft && editingSettingIndex !== settingIndex
                                ? 'Complete, update, or cancel current draft conditions before deleting another group.'
                                : 'Delete this condition group.'}
                            >
                              <GenericButton
                                buttonType={ButtonType.Ghost}
                                buttonSize={ButtonSize.Medium}
                                icon={<DeleteIcon opacity={hasPendingFiltersDraft && editingSettingIndex !== settingIndex ? '0.7' : '1'} />}
                                onClick={() => { handleOpenDeleteConfirmation(settingIndex) }}
                                disabled={hasPendingFiltersDraft && editingSettingIndex !== settingIndex}
                              />
                            </CustomTooltip>
                          </Box>
                        </Box>

                      </AccordionDetails>
                    ))}
                  </Accordion>
                )}

              <AttributesFilterForGroupSetting
                attributes={editingSettingIndex !== null ? draftSettings[editingSettingIndex]?.attributes ?? [] : []}
                onAddFilterGroup={handleAddFiltersGroup}
                actionButtonLabel={editingSettingIndex !== null ? 'Update Condition Group' : 'Add Condition Group'}
                onCancelFilterGroup={handleCancelFiltersGroup}
                disableDeleteLast={editingSettingIndex !== null}
              />

            </>
          )}

          {section?.parentSurveySectionId && (
            <>
              <Box display="flex" alignItems="center">
                <Controller
                  name="isRepeatPerInstructor"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={field.onChange}
                      color="primary"
                      disabled={isParentRepeatPerInstructor}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  )}
                />
                <Typography sx={{ ml: 2.5, fontWeight: 500 }}>
                  Repeat per Instructor ({isRepeatPerInstructor ? 'On' : 'Off'})
                </Typography>
              </Box>
              {isParentRepeatPerInstructor && (
                <Typography sx={{ mt: 1, color: grey[850] }}>
                  This section repeats for every instructor that this question is asking for.
                </Typography>
              )}
              <Box mt={2} mb={2}>
                <Typography sx={{ fontWeight: 500, mb: 1 }}>
                  Teaching Roles
                </Typography>
                <MultiSelectWithCheckBox
                  name="selectedTeachingRoles"
                  options={TEACHING_ROLE_OPTIONS}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  onSelectionChange={(selected) => {
                    setValue('selectedTeachingRoles', sortTeachingRoleOptionsByPrecedence(selected), { shouldDirty: true })
                  }}
                  placeholder="Select teaching roles"
                  renderTagsType={RenderTagsType.Chips}
                  showSelectAll
                  disabled={!isRepeatPerInstructor || isParentRepeatPerInstructor}
                  description={isParentRepeatPerInstructor
                    ? 'Teaching roles for this section are copied from the section this section is branched from.'
                    : 'Choose which teaching roles this section should be repeated for.'}
                  descriptionSx={{ color: grey[850] }}
                />
              </Box>
              <Divider sx={{ mt: 4 }} />
              <Branching section={section} />
            </>
          )}

          <DefaultModalActions
            confirmButtonProps={{
              type: 'submit',
              text: 'Save',
              buttonType: ButtonType.Primary
            }}
            destructiveButtonProps={{
              onClick: closeFilterModal
            }}
          />
          </form>
        </FormProvider>
      </GenericModal>

      <GenericModal
        title="Delete Condition Group"
        isOpen={deleteSettingIndex !== null}
        onClose={handleCloseDeleteConfirmation}
        onConfirm={handleConfirmDeleteFiltersGroup}
        confirmActionText="Delete"
        confirmActionType={ButtonType.Destructive}
        destructiveButtonText="Cancel"
        maxWidth="sm"
      >
        <Typography fontSize={14} color={grey[850]}>
          Are you sure you want to delete this condition group? This action cannot be undone.
        </Typography>
      </GenericModal>
    </>
  )
}
